import { useCallback, useId, useState, type ReactNode } from 'react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { SectionCompletion } from '../../view-model/Profile.viewModel';
import * as styles from './ProfileSection.css';

type ProfileSectionProps = {
  icon: ReactNode;
  title: string;
  description: string;
  completion: SectionCompletion;
  iconBg: string;
  iconColor: string;
  defaultOpen?: boolean;
  children: ReactNode;
};

export const ProfileSection = ({
  icon,
  title,
  description,
  completion,
  iconBg,
  iconColor,
  defaultOpen = true,
  children,
}: ProfileSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const contentId = `${generatedId}-panel`;

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        id={triggerId}
        className={styles.trigger}
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={toggle}
      >
        <div className={styles.triggerContent}>
          <div
            className={styles.triggerIcon}
            style={assignInlineVars({
              [styles.iconBgVar]: iconBg,
              [styles.iconColorVar]: iconColor,
            })}
          >
            {icon}
          </div>
          <div className={styles.triggerInfo}>
            <span className={styles.triggerTitle}>{title}</span>
            <span className={styles.triggerDescription}>{description}</span>
          </div>
        </div>
        <div className={styles.triggerRight}>
          <span
            className={styles.completionBadge({
              complete: completion.percentage === 100,
            })}
          >
            {completion.percentage}%
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={styles.chevron({ open: isOpen })}
            aria-hidden="true"
          />
        </div>
      </button>
      <div
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!isOpen}
        className={styles.panel({ open: isOpen })}
      >
        <div className={styles.panelInner}>
          <div className={styles.panelContent}>{children}</div>
        </div>
      </div>
    </div>
  );
};
